import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCPrintContainer } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import { PrintStylesAccumulatedValues } from "src/pages/Indicators/Panel/components/ModalIndicatorChart/styles";
import { IListIndicatorsChart, IListOfIndicators } from "./interface";
import executeQueryListOfIndicatorsChart from "./executeQueryListOfIndicatorsChart";
import ChartContent from "../ChartContent";
import ButtonListOfIndicators from "./ListOfIndicators";

export interface IRefListOfIndicatorsChart{
	executeQuery: () => Promise<void>;
}

const ListOfChartsIndicator: React.ForwardRefRenderFunction<IRefListOfIndicatorsChart, IListOfIndicators> = ({
	filters,
	groupersSelect,
	grouper,
	filterList,
	configs,
	configShowGoals,
	refModalValuesOfPeriodIndicators,
	printRef
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const tendencyString = translate("Tendência");

	const refsScrollArray = useRef<Array<any>>([]);

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState<IListIndicatorsChart[]>([]);

	const executeQuery = async () => {
		setLoading(true);
		async function loadRefs(listIndicators: IListIndicatorsChart[]){
			refsScrollArray.current = listIndicators.reduce((acc: any, value) => {
				acc[value.iqc001Id] = React.createRef();
				return acc;
			}, {});
		}
		try{
			const listIndicators = await executeQueryListOfIndicatorsChart(groupersSelect, {
				grouperId: grouper.id,
				grouperId2: grouper.id2,
				grouperId3: grouper.id3,
				grouperId4: undefined
			}, filters);

			await loadRefs(listIndicators);

			setList(listIndicators);

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar os indicadores"), addToast);
		}
	};

	useImperativeHandle(ref,() => ({
		executeQuery
	}));

	if(loading)
		return <Loader />;

	if(!list || list.length === 0)
		return (
			<RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>
		);

	return (
		<>
			<RCCPrintContainer
				ref={printRef}
				styleInline={PrintStylesAccumulatedValues}
				documentTitle={grouper.title}
			>
				<RCCGrid container item direction="column">
					<InfiniteScrollWithoutFetch 
						listElements={list}
						mapper={(listElement) => (<ChartContent 
							ref={refsScrollArray.current[listElement.iqc001Id]}
							key={`${listElement.iqc001Id} chartContainerIndicator`}
							data={listElement}
							tendencyString={tendencyString}
							configs={configs}
							configShowGoals={configShowGoals}
							openValuesOfPeriodIndicator={(id: number, title: string, periodId: number, isPresentation?: boolean) => {
								if(!refModalValuesOfPeriodIndicators.current)
									return;
								refModalValuesOfPeriodIndicators.current?.actionButtonOpenValuesOfPeriodIndicators(id, title, periodId, () => {
									executeQuery();
								}, isPresentation);
							}}
						/>)}
						defaultNumberOfElements={20}
						manuallyToggleShowMore
						filter={filterList ? (indicator) => indicator.iqc001Nivel === filterList : undefined} 
					/>
					<ButtonListOfIndicators onClickList={(id) => {			
						if(refsScrollArray.current.length === 0)
							return;

						if(!refsScrollArray.current[id])
							return;

						refsScrollArray.current[id].current?.scrollIntoView({ behavior: "smooth", block: "start" });
					}} data={list} />
				</RCCGrid>
			</RCCPrintContainer>
		</>
		
	);
};

export default forwardRef(ListOfChartsIndicator);
